import { clone, isArray } from 'helpers/global.helper/global.helper'
import getLogger from 'utils/log/log'

const log = getLogger('uiConfigReviewer')

/**
 * Replaces hard-coded 'regular' optjob_type values with alias references in dataset instructions to make UiConfig work for both regular and regular_wip.
 *
 *
 * @remarks
 * - Only processes dataset instructions with type 'dbs'
 * - Handles both string and array optjob_type values
 * - When optjob_type is an array, only 'regular' values within the array are replaced
 * - Automatically sets uiConfig.alias.optjob_type to 'regular' if updates were made and the alias is undefined
 * - Mutates the provided uiConfig object in place
 */
function replaceHardCodedOptJobTypeRegularToAlias(uiConfig: UiConfig, messages: string[]) {
  let hasUpdates = false

  const processDatasetInstruction = (index: number) => {
    const datasetInstruction = uiConfig.dataset_instructions[index] as DatasetInstruction

    if (datasetInstruction?.type !== 'dbs') {
      return
    }

    const contract = datasetInstruction.contract as DatasetContractDbS
    const optJobType = contract?.metadata_filter?.include?.optjob_type
    const message = `replaceHardCodedOptJobTypeRegularToAlias dataset instruction index: ${index}`

    if (!optJobType?.length) {
      return
    }

    if (typeof optJobType === 'string' && optJobType === 'regular') {
      messages.push(message)

      ;(uiConfig.dataset_instructions[index].contract as DatasetContractDbS).metadata_filter.include.optjob_type =
        '$optjob_type'
      hasUpdates = true
    } else if (isArray(optJobType) && optJobType.some((v) => v === 'regular')) {
      messages.push(message)

      ;(uiConfig.dataset_instructions[index].contract as DatasetContractDbS).metadata_filter.include.optjob_type =
        optJobType.map((v) => {
          if (v === 'regular') {
            hasUpdates = true
            return '$optjob_type'
          }

          return v
        })
    }
  }

  uiConfig.dataset_instructions?.forEach((_d, i) => processDatasetInstruction(i))

  if (hasUpdates && uiConfig.alias.optjob_type === undefined) {
    uiConfig.alias.optjob_type = 'regular'
  }
}

/**
 * We want to combine and use few, but large, dataset instructions for DbS. This will search for mergeable DSIs and combine them.
 *
 * @remarks
 * - Only looks for internal_id in metadata filter - all other fields must be synced in order for it to be flagged as "mergeable".
 */
function mergeSimilarDbSDatasetInstructions(uiConfig: UiConfig, messages: string[]) {
  const indexesToRemove = new Set<number>()
  const dsiHashToIndexMap: Record<string, number> = {}
  const hashDsi = (dsi: Partial<DatasetInstruction>): string => {
    if (dsi.type !== 'dbs') {
      throw new Error(`@mergeSimilarDbSDatasetInstructions DSI type not DbS`)
    }

    let hash = ''
    const hashValue = (value: any): string => {
      if (typeof value === 'string') {
        return value
      }

      return JSON.stringify(value)
    }

    // Filter
    const filterKeys = Object.keys(dsi.filter || {}).sort((a, b) => a.localeCompare(b)) as Array<
      keyof DatasetInstructionFilter
    >
    filterKeys.forEach((key) => {
      const value = dsi.filter?.[key]
      hash += `${key}:${hashValue(value)}`
    })

    const contract = dsi.contract as DatasetContractDbS
    const contractKeys = Object.keys(contract || {}).sort((a, b) => a.localeCompare(b)) as Array<
      keyof DatasetContractDbS
    >
    contractKeys.forEach((key) => {
      const value = contract[key]

      if (key === 'id_renaming') {
        return
      }

      if (key === 'metadata_filter') {
        const metadataFilterKeys = Object.keys(contract.metadata_filter.include || {}).sort((a, b) =>
          a.localeCompare(b)
        )
        metadataFilterKeys.forEach((metadataKey) => {
          if (metadataKey === 'internal_id') {
            return
          }

          const metaValue = contract.metadata_filter.include[metadataKey]
          hash += `${metadataKey}:${hashValue(metaValue)}`
        })

        return
      }

      hash += `${key}:${hashValue(value)}`
    })

    return hash
  }
  const mergeInternalIds = (ids1: string | string[], ids2: string | string[]): string[] => {
    if (typeof ids1 === 'string') {
      ids1 = [ids1]
    }
    if (typeof ids2 === 'string') {
      ids2 = [ids2]
    }

    return [...ids1, ...ids2]
  }

  if (!uiConfig?.dataset_instructions?.length) {
    return
  }

  for (let i = 0; i < uiConfig.dataset_instructions.length; i++) {
    const dsi = uiConfig.dataset_instructions[i]

    if (dsi.type !== 'dbs' || !(dsi.contract as DatasetContractDbS)?.metadata_filter?.include?.internal_id?.length) {
      continue
    }

    const hash = hashDsi(dsi)

    if (dsiHashToIndexMap[hash] !== undefined) {
      const mergeTargetIndex = dsiHashToIndexMap[hash]
      indexesToRemove.add(i)
      messages.push(`mergeSimilarDbSDatasetInstructions DSI index ${i} merged into ${mergeTargetIndex}`)

      // Merge metadata_filter.include.internal_id
      ;(
        uiConfig.dataset_instructions[mergeTargetIndex].contract as DatasetContractDbS
      ).metadata_filter.include.internal_id = mergeInternalIds(
        (uiConfig.dataset_instructions[mergeTargetIndex].contract as DatasetContractDbS).metadata_filter.include
          .internal_id,
        (dsi.contract as DatasetContractDbS).metadata_filter.include.internal_id
      )

      // Merge id_renaming
      const idRenaming = {
        ...((uiConfig.dataset_instructions[mergeTargetIndex].contract as DatasetContractDbS).id_renaming || {}),
        ...((dsi.contract as DatasetContractDbS).id_renaming || {}),
      }
      if (Object.keys(idRenaming).length > 0) {
        ;(uiConfig.dataset_instructions[mergeTargetIndex].contract as DatasetContractDbS).id_renaming = idRenaming
      }
    } else {
      dsiHashToIndexMap[hash] = i
    }
  }

  uiConfig.dataset_instructions = uiConfig.dataset_instructions.filter((_d, i) => !indexesToRemove.has(i))
}

export function autoFixUiConfig<T extends UiConfig | null | undefined>(
  uiConfig: T
): {
  autoFixedUiConfig: T
  hasUpdates: boolean
  messages: string[]
} {
  if (!uiConfig) {
    return {
      autoFixedUiConfig: uiConfig,
      hasUpdates: false,
      messages: [],
    }
  }

  try {
    const autoFixedUiConfig = clone(uiConfig)
    const messages: string[] = []

    // Checks
    replaceHardCodedOptJobTypeRegularToAlias(autoFixedUiConfig, messages)
    mergeSimilarDbSDatasetInstructions(autoFixedUiConfig, messages)

    return {
      autoFixedUiConfig,
      hasUpdates: messages.length > 0,
      messages,
    }
  } catch (err: any) {
    log.error(`Failed to auto-fix ui config ${uiConfig?.id}`, err)

    return {
      autoFixedUiConfig: uiConfig,
      hasUpdates: false,
      messages: [],
    }
  }
}
